import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NzFormModule } from 'ng-zorro-antd/form';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { EMPTY, INSTAGRAM_HOST, MENTION_SYMBOL } from '../../util/const';
import { USERNAME_REGEXP } from '../../validation/const';
import { Router } from '@angular/router';
import { AnalyticsService } from '../../service/analytics.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { unsubscribe } from '../../util/rx.util';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-search-form',
  imports: [
    NzFormModule,
    ReactiveFormsModule,
    NzInputGroupComponent,
    NzInputDirective,
    NzButtonComponent,
    NzIconDirective,
  ],
  templateUrl: './search-form.component.html',
  styleUrl: './search-form.component.less',
})
export class SearchFormComponent implements OnInit, OnDestroy {
  public readonly valueConsumer: InputSignal<
    BehaviorSubject<string> | undefined
  > = input<BehaviorSubject<string>>();
  public readonly formGroup: FormGroup = new FormGroup({
    username: new FormControl({ value: EMPTY, disabled: false }, [
      Validators.pattern(USERNAME_REGEXP),
    ]),
  });
  private readonly router: Router = inject(Router);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private valueSub$?: Subscription = undefined;

  public ngOnInit(): void {
    const valueConsumer$: BehaviorSubject<string> | undefined =
      this.valueConsumer();
    if (valueConsumer$) {
      this.valueSub$ = valueConsumer$.subscribe((value: string): void => {
        this.formGroup.patchValue({ username: value });
      });
    }
  }

  public ngOnDestroy(): void {
    unsubscribe(this.valueSub$);
  }

  public submitForm(): void {
    if (this.formGroup.invalid) {
      return;
    }
    const username: string =
      this.formGroup.get('username')!.value?.trim()?.toLowerCase() ?? EMPTY;
    if (!username) {
      return;
    }
    this.router.navigate(['profiles', username]).then((): void => {
      this.analyticsService.event('user_exec', {
        origin: 'search',
        username: username,
        path: this.router.url,
      });
    });
  }

  public onSearchInput(event: Event): void {
    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;
    let value: string = htmlInputElement.value?.trim()?.toLowerCase();
    if (value.startsWith(MENTION_SYMBOL)) {
      value = value.substring(1);
    }
    if (value.includes(INSTAGRAM_HOST)) {
      const urlParts: string[] = value.split('/');
      value = urlParts[urlParts.length - 1] ?? EMPTY;
    }
    this.formGroup.patchValue({ username: value });
  }
}
